// src/components/shared/CourseCard/hooks/useCourseCard.ts
import { useRef, type RefObject } from 'react';
import { getCourseTypeStyle, getCourseType, type CourseType } from '../utils';
import type { LucideIcon } from 'lucide-react';
import type { CourseDragBind } from '../../../../contexts/DragDropContext';

export interface CourseCardGradeData {
  distribution?: Record<string, number | undefined> | null;
  total_students?: number | null;
  average_gpa?: number | null;
  difficulty?: string | null;
}

export interface CourseCardCourse {
  code: string;
  course_code?: string;
  title?: string;
  course_title?: string;
  credits?: number;
  isSuggestion?: boolean;
  isPlaceholder?: boolean;
  description?: string;
  /** Legacy field, unused by the card (the plan payload sends the parsed
   *  tree here). The card renders the `*_raw` catalog text below. */
  prerequisites?: unknown;
  /** Catalog requisite text, verbatim — shown in the expanded view. */
  prerequisites_raw?: string | null;
  corequisites_raw?: string | null;
  restriction?: string | null;
  gen_ed_requirements?: string[];
  /** The resolved single-area-per-OR-group choice (from `gen_ed_override`).
   *  Used to render the OR select CONTROLLED (value = the actual pick), while
   *  `gen_ed_requirements` stays raw so the OR options are known. */
  gen_ed_resolved?: string[];
  grade_required?: string;
  grade?: string | null;
  status?: string;
  grade_data?: CourseCardGradeData | null;
  requirementKey?: string | null;
  genEdArea?: string | null;
  genEdAlternatives?: string[];
  // Optional placeholder data (for placeholder variant)
  requirements?: { department?: string; [key: string]: unknown };
}

export interface CourseAction {
  type: string;
  label?: string;
  value?: unknown;
  confirmable?: boolean;
}

export interface CustomCourseAction {
  action: string;
  label: string;
  icon?: LucideIcon;
  className?: string;
}

export interface RequirementTag {
  label: string;
  [key: string]: unknown;
}

export type GradeOption = string;

export interface PrereqWarning {
  course_code?: string;
  prereq_code?: string;
  earned_grade?: string;
  required_grade?: string;
  message?: string;
}

export interface RetakeCandidate {
  course_code?: string;
  earned_grade?: string;
  reason?: string;
  dependent_courses?: string[];
}

export interface DropIndicator {
  before?: boolean;
  after?: boolean;
  [key: string]: unknown;
}

export type CheckAvailabilityFn = (
  courseCode: string,
  semester: string
) => Promise<unknown>;

export interface CourseCardProps {
  course: CourseCardCourse;
  variant?: string;
  semester?: string;
  index?: number;
  actions?: CourseAction[];
  customActions?: CustomCourseAction[];
  onAction?: (actionType: string, value?: unknown) => void;
  // UI Tier 3.1b: dnd-kit drag source bind (ref + listeners + aria attributes)
  // supplied by <DraggableCourse>; applied to the card's drag grip.
  dragBind?: CourseDragBind;
  isDragging?: boolean;
  openDropdownId?: string | null;
  setOpenDropdownId?: (id: string | null) => void;
  contextId?: string;
  dropdownContext?: string;
  showCredits?: boolean;
  showDescription?: boolean;
  showPrerequisites?: boolean;
  showBadges?: string[];
  showBadgeIcons?: boolean;
  showActionsMenu?: boolean;
  isExpandable?: boolean;
  isDraggable?: boolean;
  className?: string;
  requirementTags?: RequirementTag[];
  checkAvailability?: CheckAvailabilityFn | null;
  showGradeInput?: boolean;
  grade?: string | null;
  onGradeChange?: (grade: string | null) => void;
  prereqWarning?: PrereqWarning | null;
  retakeCandidate?: RetakeCandidate | null;
  courseRoles?: string[];
  [key: string]: unknown;
}

export interface UseCourseCardReturn {
  course: CourseCardCourse;
  courseType: CourseType;
  typeStyle: string;
  variant?: string;
  showCredits: boolean;
  showDescription: boolean;
  showPrerequisites: boolean;
  showBadges: string[];
  showBadgeIcons: boolean;
  showActionsMenu: boolean;
  isExpandable: boolean;
  className: string;
  requirementTags?: RequirementTag[];
  isDraggable: boolean;
  isDragging?: boolean;
  actions?: CourseAction[];
  customActions?: CustomCourseAction[];
  onAction?: (actionType: string, value?: unknown) => void;
  dropdownRef: RefObject<HTMLDivElement>;
  toggleDropdown: (e?: { stopPropagation?: () => void }) => void;
  openDropdownId?: string | null;
  setOpenDropdownId?: (id: string | null) => void;
  dragBind?: CourseDragBind;
  semester?: CourseCardProps['semester'];
  index?: number;
  contextId?: string;
  dropdownContext?: string;
  checkAvailability?: CheckAvailabilityFn | null;
  showGradeInput: boolean;
  grade?: string | null;
  onGradeChange?: (grade: string | null) => void;
  prereqWarning?: PrereqWarning | null;
  retakeCandidate?: RetakeCandidate | null;
  courseRoles?: string[];
}

export const useCourseCard = (props: CourseCardProps): UseCourseCardReturn => {
  const {
    course,
    variant,
    semester,
    index,
    actions,
    customActions,
    onAction,
    dragBind,
    isDragging,
    openDropdownId,
    setOpenDropdownId,
    contextId,
    dropdownContext,
    showCredits = true,
    showDescription = false,
    showPrerequisites = false,
    showBadges = ['type'],
    showBadgeIcons = false,
    isExpandable = false,
    isDraggable = true,
    className = '',
    requirementTags,
    checkAvailability,
    showGradeInput = false,
    grade,
    onGradeChange,
    prereqWarning,
    retakeCandidate,
    courseRoles,
  } = props;

  const dropdownRef = useRef<HTMLDivElement>(null);

  const courseType = getCourseType(course?.code || course?.course_code, course);
  const typeStyle = getCourseTypeStyle(courseType);

  const toggleDropdown = (e?: { stopPropagation?: () => void }): void => {
    e?.stopPropagation?.();
    if (setOpenDropdownId) {
      setOpenDropdownId(openDropdownId === contextId ? null : (contextId ?? null));
    }
  };

  return {
    course,
    courseType,
    typeStyle,

    variant,
    showCredits,
    showDescription,
    showPrerequisites,
    showBadges,
    showBadgeIcons,
    showActionsMenu: openDropdownId === contextId,
    isExpandable,
    className,
    requirementTags,

    isDraggable,
    isDragging,

    actions,
    customActions,
    onAction,

    dropdownRef,
    toggleDropdown,
    openDropdownId,
    setOpenDropdownId,

    dragBind,

    semester,
    index,
    contextId,
    dropdownContext,

    checkAvailability,

    showGradeInput,
    grade,
    onGradeChange,

    prereqWarning,
    retakeCandidate,

    courseRoles,
  };
};
